import { Shape } from "three";
// eslint-disable-next-line boundaries/element-types
import { Clipper, JoinType } from "../../clipper";
import { InvalidSvgShapeAssetError } from "../../flux_error";
import { centerShapePoints } from "../helpers/centerShape";
import { FluxSvgLoader } from "./fluxSvgLoader";
const RADIAL_CURVES = ["EllipseCurve", "ArcCurve"];
const COMPLETE_CIRCLE = Math.PI * 2;
const DEFAULT_POLYGON_SEGMENTS = 16;
// When the SVG includes a <circle> or <ellipse> element with a full rotation
function isClosedRadialShape(path) {
    if (path.subPaths.length !== 1 || path.subPaths[0].curves?.length !== 1) {
        return false;
    }
    const curve = path.subPaths[0].curves[0];
    return RADIAL_CURVES.includes(curve.type) && curve.aEndAngle - curve.aStartAngle === COMPLETE_CIRCLE;
}
// When the SVG includes a path that ends with the Z/z directive
function isAutoClosedPath(path) {
    return path.currentPath?.autoClose;
}
// When the SVG uses a <rect> element
function firstAndLastPointAreEqual(shapePoints) {
    const firstPoint = shapePoints.at(0);
    const lastPoint = shapePoints.at(-1);
    return shapePoints.length > 0 && firstPoint.equals(lastPoint);
}
function inputIsClosed(path) {
    return isAutoClosedPath(path) || isClosedRadialShape(path);
}
function outputIsClosed(shapePoints) {
    return firstAndLastPointAreEqual(shapePoints);
}
/**
 * Parses an SVG file and ensures it contains exactly one closed shape. Useful for cases where
 * a closed shape is essential. eg. PCB Layout
 *
 * Verifies that the shape is closed by validating the input (SVG) or output (ShapePoints) of the shape.
 *
 * @param svgString The string representation of an SVG file
 * @param opts Various parsing and processing options
 * @returns The parsed, closed shape from the SVG file
 */
export function createShapeFromSvgString(svgString, opts = {}) {
    const loader = new FluxSvgLoader();
    let svgData;
    try {
        svgData = loader.parse(svgString);
    }
    catch (error) {
        throw new InvalidSvgShapeAssetError(`Error parsing SVG asset`, error);
    }
    const paths = svgData.paths;
    if (paths.length !== 1) {
        throw new InvalidSvgShapeAssetError();
    }
    const path = paths[0];
    const shapes = FluxSvgLoader.createShapes(path);
    if (shapes.length !== 1) {
        throw new InvalidSvgShapeAssetError();
    }
    const shapePoints = shapes[0].getPoints();
    const closedShape = inputIsClosed(path) || outputIsClosed(shapePoints);
    if (!closedShape) {
        throw new InvalidSvgShapeAssetError();
    }
    if (opts.yIsUp ?? true) {
        // flip y axis because svgs are positive y axis down and in flux its up
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        shapePoints.forEach((p) => (p.y *= -1));
    }
    if (opts.center) {
        centerShapePoints(shapePoints);
    }
    let shape = new Shape();
    shape.setFromPoints(shapePoints);
    // When rendering the SVG shapes into points, ThreeJS relies on the shape being autoclosed to
    // complete the shape in cases where the final path is a curve or some other complex geomotery.
    // Precision errors can cause the last point to not be exactly equal to the first point, so we
    // force it to be closed after verifying that the intention was to close the shape.
    shape.autoClose = true;
    const scale = opts.scale;
    if (scale) {
        const points = shape
            .getPoints(opts.polygonSegments ?? DEFAULT_POLYGON_SEGMENTS)
            .map((point) => point.multiply(scale));
        shape = new Shape(points);
    }
    const expansion = opts.expansion;
    if (expansion) {
        return Clipper.fromShape(shape).offset(expansion, { joinType: JoinType.Round }).toShape(0);
    }
    return shape;
}
//# sourceMappingURL=createShapeFromSvgString.js.map